/**
 * Sign-in screen for the mob PWA. Two paths:
 *
 *   1. Passkey — biometric sign-in via WebAuthn. RP ID is the registrable
 *      parent domain so credentials registered on the dashboard work here.
 *      Once successful, the Worker sets the cross-subdomain cookie and the
 *      app loads inbox.
 *
 *   2. Password fallback — bounces to the main dashboard's /login with
 *      ?return=<here>. The dashboard signs in normally, sets the cookie,
 *      browser comes back, mob's subsequent API calls auth via cookie.
 *
 * Passkey is the primary CTA on the Z Flip — face/fingerprint is one tap.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fingerprint, Lock, AlertTriangle } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON } from '@simplewebauthn/types';
import { api, redirectToLogin, apiBase } from '../lib/api';

export function Login() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function passkey() {
    setErr(null);
    setBusy(true);
    try {
      // Discoverable-credential flow — no email needed; the browser shows
      // the user's available passkeys for this domain.
      const optsResp = await fetch(`${apiBase}/auth/passkey/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!optsResp.ok) throw new Error(`HTTP ${optsResp.status}`);
      const optsBody = await optsResp.json() as {
        options: Parameters<typeof startAuthentication>[0]['optionsJSON'];
        challengeJwt: string;
      };
      const response: AuthenticationResponseJSON = await startAuthentication({
        optionsJSON: optsBody.options,
      });
      // Verify — the Worker sets the .nightshade.gameonsolutions.uk session
      // cookie on success, so subsequent /research calls authenticate.
      await api.post('/auth/passkey/login/verify', {
        response,
        challengeJwt: optsBody.challengeJwt,
      });
      nav('/', { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="flex flex-col items-center gap-2">
          <Lock className="h-7 w-7 text-accent" />
          <h1 className="font-serif text-2xl">Nightshade Research</h1>
          <p className="text-sm text-muted">
            Use a passkey for one-tap sign-in, or fall back to the dashboard
            for password login.
          </p>
        </div>

        <button onClick={passkey} disabled={busy} className="btn-primary w-full py-4 text-base flex items-center gap-2 justify-center">
          <Fingerprint className="h-5 w-5" />
          {busy ? 'Waiting on device…' : 'Sign in with passkey'}
        </button>

        {err && (
          <div className="flex items-start gap-2 text-sm text-danger text-left">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="break-all">{err}</span>
          </div>
        )}

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center">
            <span className="bg-bg px-3 text-xs uppercase tracking-wider text-muted">or</span>
          </div>
        </div>

        <button onClick={redirectToLogin} className="btn-ghost w-full py-3">
          Sign in with password
        </button>
        <p className="text-xs text-muted">
          You'll be sent to{' '}
          <span className="font-mono">nightshade.gameonsolutions.uk</span>
          {' '}and bounced back here once you're in.
        </p>
      </div>
    </div>
  );
}
